/**
 * Specifications API Service
 *
 * API functions for PowerSpec V3 Specification Library
 * Part of spec-driven development workflow
 *
 * Standards: .equilateral-standards/serverless-saas-aws/api_standards.md
 */

import { apiClient, Make_Authorized_API_Call, type TimComboAPIResponse } from './apiClient';
import { API_ENDPOINTS } from '../services/api/endpoints';

// Re-export response types for consumers
export type { TimComboAPIResponse };

// Types
export interface Specification {
  id: string;
  business_id: string;
  title: string;
  content: Record<string, unknown> | null;
  content_markdown: string | null;
  content_text: string | null;
  status: 'draft' | 'in_review' | 'changes_requested' | 'approved' | 'published' | 'archived' | 'outdated';
  tags: string[];
  version: number;
  edit_count: number;
  view_count: number;
  connected_repo: string | null;
  repo_paths: string[] | null;
  parent_spec_id: string | null;
  metadata: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
  approved_at: string | null;
  approved_by: string | null;
  template_id: string | null;
  template_name?: string;
  template_category?: string;
  client_id: string;
  project_id: string | null;
}

export interface SpecificationTemplate {
  id: string;
  business_id: string;
  name: string;
  description: string;
  category: string;
  structure?: Record<string, unknown>;
  example_content?: string;
  is_system_template: boolean;
  is_public: boolean;
  usage_count: number;
  rating_average: number | null;
  rating_count: number;
  created_at: string;
  updated_at: string;
}

export interface CreateTemplateRequest {
  Client_ID: string;
  name: string;
  description?: string;
  category: string;
  structure: Record<string, unknown>;
  example_content?: Record<string, unknown>;
  is_public?: boolean;
}

export interface UpdateTemplateRequest {
  Client_ID: string;
  template_id: string;
  name?: string;
  description?: string;
  category?: string;
  structure?: Record<string, unknown>;
  example_content?: Record<string, unknown>;
  is_public?: boolean;
}

export interface SpecificationReview {
  id: string;
  spec_id: string;
  reviewer_email: string;
  review_type: string;
  status: 'pending' | 'completed';
  decision: 'approved' | 'rejected' | 'changes_requested' | null;
  comment: string | null;
  due_date: string | null;
  reviewed_at: string | null;
}

export interface ConsensusSummary {
  approved: number;
  rejected: number;
  changes_requested: number;
  pending: number;
  total: number;
  approval_percentage: number;
  has_consensus: boolean;
}

export interface CreateSpecificationRequest {
  Client_ID: string;
  Project_ID?: string;
  template_id?: string;
  title: string;
  content?: Record<string, unknown>;
  content_markdown?: string;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface UpdateSpecificationRequest {
  title?: string;
  content?: Record<string, unknown>;
  content_markdown?: string;
  status?: Specification['status'];
  tags?: string[];
  connected_repo?: string;
  repo_paths?: string[];
  metadata?: Record<string, unknown>;
  create_version?: boolean;
}

export interface RequestReviewRequest {
  reviewer_emails: string[];
  review_type?: 'general' | 'technical' | 'editorial' | 'security' | 'compliance';
  due_date?: string;
  message?: string;
}

export interface SubmitApprovalRequest {
  decision: 'approved' | 'rejected' | 'changes_requested';
  comment?: string;
}

export interface EmbellishmentRequest {
  embellishment_types: ('technical_depth' | 'risk_analysis' | 'dependency_suggestions' | 'acceptance_criteria' | 'test_cases')[];
  context?: {
    connected_repo?: string;
    related_specs?: string[];
  };
}

export interface RepositoryLinkRequest {
  repository_id: string;
  repo_paths?: string[];
  scan_on_link?: boolean;
}

// =============================================================================
// Bulk Import Types and Functions (Onboarding)
// =============================================================================

/**
 * Individual spec for bulk import
 */
export interface ImportSpecItem {
  title: string;
  content_markdown: string;
  tags?: string[];
  template_category?: string;
}

/**
 * Request body for bulk spec import
 */
export interface ImportSpecRequest {
  Client_ID: string;
  specs: ImportSpecItem[];
}

/**
 * Bulk import specifications (onboarding flow)
 * POST /api/specifications/specificationImportPost
 */
export async function importSpecifications(
  data: ImportSpecRequest
): Promise<TimComboAPIResponse<Specification>> {
  return Make_Authorized_API_Call<Specification>(
    API_ENDPOINTS.SPECIFICATIONS.IMPORT,
    'POST',
    data
  );
}

// API Functions

/**
 * Get list of specifications with filtering and pagination
 */
export async function getSpecifications(params: {
  Client_ID: string;
  Project_ID?: string;
  status?: string;
  template_id?: string;
  search?: string;
  page?: number;
  limit?: number;
}) {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, String(value));
    }
  });

  return apiClient.get<{ Records: Specification[] }>(
    `/tim/specifications?${queryParams.toString()}`
  );
}

/**
 * Get a single specification by ID
 */
export async function getSpecification(specId: string, includeContent = true) {
  const queryParams = new URLSearchParams();
  if (includeContent) {
    queryParams.append('include_content', 'true');
  }
  return apiClient.get<{ Records: Specification[] }>(
    `/tim/specifications/${specId}?${queryParams.toString()}`
  );
}

/**
 * Create a new specification
 */
export async function createSpecification(data: CreateSpecificationRequest) {
  return apiClient.post<{ Records: Specification[] }>(
    '/tim/specifications',
    data
  );
}

/**
 * Update an existing specification
 */
export async function updateSpecification(specId: string, data: UpdateSpecificationRequest) {
  return apiClient.put<{ Records: Specification[] }>(
    `/tim/specifications/${specId}`,
    data
  );
}

/**
 * Delete (archive) a specification
 */
export async function deleteSpecification(specId: string, permanent = false) {
  const queryParams = permanent ? '?permanent=true' : '';
  return apiClient.delete<{ Records: { id: string; business_id: string; deleted?: boolean }[] }>(
    `/tim/specifications/${specId}${queryParams}`
  );
}

/**
 * Get specification templates
 */
export async function getSpecificationTemplates(params?: {
  category?: string;
  include_structure?: boolean;
  brand_id?: string;
}) {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }
  return apiClient.get<{
    Records: SpecificationTemplate[];
    by_category: Record<string, SpecificationTemplate[]>;
  }>(`/tim/specification-templates?${queryParams.toString()}`);
}

/**
 * Create a custom specification template
 * POST /api/specifications/specificationTemplateCreate
 */
export async function createSpecificationTemplate(data: CreateTemplateRequest) {
  return Make_Authorized_API_Call<SpecificationTemplate>(
    API_ENDPOINTS.SPECIFICATIONS.TEMPLATE_CREATE,
    'POST',
    data
  );
}

/**
 * Update a custom specification template
 * PUT /api/specifications/specificationTemplateUpdate
 */
export async function updateSpecificationTemplate(templateId: string, data: Omit<UpdateTemplateRequest, 'template_id'>) {
  return Make_Authorized_API_Call<SpecificationTemplate>(
    API_ENDPOINTS.SPECIFICATIONS.TEMPLATE_UPDATE,
    'PUT',
    { ...data, template_id: templateId }
  );
}

/**
 * Request a review for a specification
 */
export async function requestSpecificationReview(specId: string, data: RequestReviewRequest) {
  return apiClient.post<{
    Records: {
      spec_id: string;
      business_id: string;
      title: string;
      status: string;
      review_requests: SpecificationReview[];
      reviewers_count: number;
    }[];
  }>(`/tim/specifications/${specId}/request-review`, data);
}

/**
 * Submit an approval decision for a specification
 */
export async function submitSpecificationApproval(specId: string, data: SubmitApprovalRequest) {
  return apiClient.post<{
    Records: {
      spec_id: string;
      business_id: string;
      title: string;
      your_decision: string;
      spec_status: string;
      consensus_reached: boolean;
      consensus_summary: ConsensusSummary;
    }[];
  }>(`/tim/specifications/${specId}/approval`, data);
}

/**
 * Get approval status for a specification
 */
export async function getSpecificationApprovalStatus(specId: string) {
  return apiClient.get<{
    Records: {
      spec_id: string;
      business_id: string;
      title: string;
      current_status: string;
      current_version: number;
      approved_at: string | null;
      approved_by: string | null;
      reviews: SpecificationReview[];
      consensus: ConsensusSummary;
    }[];
  }>(`/tim/specifications/${specId}/approval-status`);
}

/**
 * Embellish a specification with AI
 */
export async function embellishSpecification(specId: string, data: EmbellishmentRequest) {
  return apiClient.post<{
    Records: {
      spec_id: string;
      embellishments: {
        type: string;
        content: string;
        confidence_score: number;
      }[];
    }[];
  }>(`/tim/specifications/${specId}/embellish`, data);
}

/**
 * Link a specification to a repository
 */
export async function linkSpecificationRepository(specId: string, data: RepositoryLinkRequest) {
  return apiClient.post<{
    Records: {
      spec_id: string;
      repository_id: string;
      repo_paths: string[];
      scan_triggered: boolean;
    }[];
  }>(`/tim/specifications/${specId}/repository`, data);
}

/**
 * Get linked repository for a specification
 */
export async function getSpecificationRepository(specId: string) {
  return apiClient.get<{
    Records: {
      spec_id: string;
      repository_id: string;
      repository_name: string;
      repository_url: string;
      repository_provider: string;
      repo_paths: string[];
      last_scan_date: string | null;
    }[];
  }>(`/tim/specifications/${specId}/repository`);
}

/**
 * Trigger a codebase scan
 */
export async function triggerCodebaseScan(data: {
  repo_url: string;
  Client_ID: string;
  Project_ID?: string;
  branch?: string;
}) {
  return apiClient.post<{
    Records: {
      id: string;
      business_id: string;
      repo_url: string;
      repo_name: string;
      branch: string;
      scan_status: string;
      scans_remaining: number | 'unlimited';
      insights_will_be_visible: boolean;
    }[];
  }>('/tim/codebase/scan', data);
}

/**
 * Generate specifications from codebase scan results
 * Uses AI to analyze scan insights and create draft specifications
 */
export interface GenerateFromCodebaseRequest {
  Client_ID: string;
  Project_ID?: string;
  scan_id?: string;
  repo_url?: string;
  spec_types?: ('tech_stack' | 'architecture' | 'api_docs' | 'data_model' | 'testing_strategy')[];
}

export interface GeneratedSpec {
  id: string;
  business_id: string;
  title: string;
  status: string;
  tags: string[];
  version: number;
  created_at: string;
  connected_repo: string;
}

export interface GenerationResult {
  spec_type: string;
  spec_id: string;
  business_id: string;
  title: string;
  status: 'completed' | 'failed';
  error?: string;
}

export interface GenerateFromCodebaseResponse {
  Records: GeneratedSpec[];
  Total_Records: number;
  Success_Count: number;
  Failure_Count: number;
  Scan_ID: string;
  Scan_Business_ID: string;
  Repository: string;
  Spec_Types_Generated: string[];
  Generation_Results: GenerationResult[];
}

export async function generateSpecificationsFromCodebase(data: GenerateFromCodebaseRequest) {
  return apiClient.post<GenerateFromCodebaseResponse>(
    '/api/specifications/specificationGenerateFromCodebase',
    data
  );
}

// =============================================================================
// Spec Version History Types and Functions
// =============================================================================

/**
 * Version history record for a specification
 */
export interface VersionRecord {
  version_id: string;
  version: number;
  change_type: string;
  change_summary: string | null;
  changed_by: string;
  created_at: string;
  content_preview?: string | null;
}

/**
 * Create a version snapshot for a specification
 * POST /api/specifications/specificationVersionCreatePost
 */
export async function createSpecificationVersion(
  specId: string,
  data: {
    Client_ID: string;
    change_type?: 'create' | 'update' | 'status_change' | 'approval';
    change_summary?: string;
  }
): Promise<TimComboAPIResponse<VersionRecord>> {
  return Make_Authorized_API_Call<VersionRecord>(
    API_ENDPOINTS.SPECIFICATIONS.VERSION_CREATE,
    'POST',
    {
      spec_id: specId,
      Client_ID: data.Client_ID,
      change_type: data.change_type || 'update',
      change_summary: data.change_summary
    }
  );
}

// =============================================================================
// Notification Types and Functions
// =============================================================================

/**
 * Specification notification record (drift alerts, approval changes, etc.)
 */
export interface SpecNotification {
  Notification_ID: number;
  Notification_Type: 'drift_detected' | 'scan_complete' | 'approval_requested' | 'approval_submitted'
    | 'spec_published' | 'spec_outdated' | 'embellishment_ready' | 'dependency_change';
  Title: string;
  Message: string | null;
  Spec_ID: string | null;
  Spec_Title: string | null;
  Severity: 'info' | 'warning' | 'error' | 'success';
  Status: 'unread' | 'read' | 'dismissed';
  Metadata: Record<string, unknown>;
  Created_At: string;
  Read_At: string | null;
}

/**
 * Get spec notifications for a client (polling fallback for real-time push)
 * GET /api/specifications/specificationNotificationsGet
 */
export async function getSpecNotifications(
  clientId: string,
  params?: { since?: string; status?: 'unread' | 'read' | 'all' }
): Promise<TimComboAPIResponse<SpecNotification>> {
  const queryParams: Record<string, string> = {
    Client_ID: clientId
  };

  if (params?.since) queryParams.since = params.since;
  if (params?.status) queryParams.status = params.status;

  return Make_Authorized_API_Call<SpecNotification>(
    API_ENDPOINTS.SPECIFICATIONS.NOTIFICATIONS,
    'GET',
    undefined,
    { params: queryParams }
  );
}

// =============================================================================
// Dependency Matrix Types and Functions
// =============================================================================

/**
 * Code dependency record from the dependency matrix
 */
export interface CodeDependency {
  Dependency_ID: string;
  Source_Type: 'spec' | 'file' | 'function' | 'class';
  Source_ID: string;
  Source_Name: string | null;
  Target_Type: 'spec' | 'file' | 'function' | 'class';
  Target_ID: string;
  Target_Name: string | null;
  Relationship_Type: 'imports' | 'calls' | 'implements' | 'extends' | 'tests' | 'documents' | 'related_to' | 'blocks' | 'depends_on';
  Strength: 'direct' | 'indirect' | 'suggested';
  Confidence_Score: number;
  Discovered_By: 'scan' | 'manual' | 'ai';
  Discovered_At: string;
}

/**
 * Request body for creating a code dependency
 */
export interface CreateDependencyRequest {
  Client_ID: string;
  source_type: CodeDependency['Source_Type'];
  source_id: string;
  source_name?: string;
  target_type: CodeDependency['Target_Type'];
  target_id: string;
  target_name?: string;
  relationship_type: CodeDependency['Relationship_Type'];
  strength?: CodeDependency['Strength'];
  confidence_score?: number;
  discovered_by?: CodeDependency['Discovered_By'];
}

/**
 * Get code dependencies for a specification or client
 * GET /api/specifications/codeDependenciesGet
 */
export async function getCodeDependencies(
  specId: string,
  params?: {
    Client_ID: string;
    element_type?: string;
    relationship_type?: string;
    min_confidence?: number;
  }
): Promise<TimComboAPIResponse<CodeDependency>> {
  const queryParams: Record<string, string> = {
    Client_ID: params?.Client_ID || '',
    spec_id: specId
  };

  if (params?.element_type) queryParams.element_type = params.element_type;
  if (params?.relationship_type) queryParams.relationship_type = params.relationship_type;
  if (params?.min_confidence !== undefined) queryParams.min_confidence = params.min_confidence.toString();

  return Make_Authorized_API_Call<CodeDependency>(
    API_ENDPOINTS.SPECIFICATIONS.DEPENDENCIES_GET,
    'GET',
    undefined,
    { params: queryParams }
  );
}

/**
 * Create a code dependency
 * POST /api/specifications/codeDependencyCreatePost
 */
export async function createCodeDependency(
  data: CreateDependencyRequest
): Promise<TimComboAPIResponse<CodeDependency>> {
  return Make_Authorized_API_Call<CodeDependency>(
    API_ENDPOINTS.SPECIFICATIONS.DEPENDENCY_CREATE,
    'POST',
    data
  );
}

// =============================================================================
// CPO (Cost/Performance/Optimization) Types and Functions
// =============================================================================

/**
 * Specification execution cost record
 */
export interface SpecCost {
  Spec_Cost_ID: string;
  Specification_ID: string;
  Execution_ID: string;
  Model_Name: string;
  Model_Type: 'opus' | 'sonnet' | 'haiku';
  Input_Tokens: number;
  Output_Tokens: number;
  Total_Cost: number;
  Operation_Type: string;
  Create_Date: string;
}

/**
 * Model distribution breakdown
 */
export interface ModelDistribution {
  model: string;
  model_type: 'opus' | 'sonnet' | 'haiku';
  execution_count: number;
  total_cost: number;
  percentage: number;
}

/**
 * Specification cost summary
 */
export interface SpecCostSummary {
  Specification_ID: string;
  Spec_Title: string;
  Total_Executions: number;
  Total_Cost: number;
  Avg_Cost_Per_Execution: number;
  Model_Distribution: ModelDistribution[];
  Period_Start: string;
  Period_End: string;
  Trend?: {
    Previous_Period_Cost: number;
    Change_Percentage: number;
    Direction: 'up' | 'down' | 'stable';
  };
}

/**
 * Specification recommendation
 */
export interface SpecRecommendation {
  Recommendation_ID: string;
  Specification_ID: string;
  Recommendation_Type: 'cost_optimization' | 'quality_improvement' | 'security' | 'performance';
  Priority: 'high' | 'medium' | 'low';
  Title: string;
  Description: string;
  Potential_Savings?: number;
  Estimated_Impact?: string;
  Status: 'pending' | 'acknowledged' | 'implemented' | 'dismissed';
  Create_Date: string;
  Acknowledged_At?: string;
  Acknowledged_By?: string;
}

/**
 * Specification metrics (dashboard level)
 */
export interface SpecMetrics {
  Total_Specifications: number;
  Total_Executions: number;
  Total_Cost: number;
  Avg_Cost_Per_Spec: number;
  Active_Recommendations: number;
  Potential_Savings: number;
  Top_Cost_Specs: Array<{
    Specification_ID: string;
    Title: string;
    Total_Cost: number;
  }>;
  Cost_By_Period: Array<{
    period: string;
    cost: number;
    executions: number;
  }>;
}

/**
 * Cost query parameters
 */
export interface SpecCostQueryParams {
  period_start?: string;
  period_end?: string;
  include_breakdown?: boolean;
  limit?: number;
  offset?: number;
}

/**
 * Get execution costs for a specification
 * GET /api/specifications/specificationCostsGet
 */
export async function getSpecCosts(
  specificationId: string,
  params?: SpecCostQueryParams
): Promise<TimComboAPIResponse<SpecCost>> {
  const queryParams: Record<string, string> = {
    Specification_ID: specificationId
  };

  if (params?.period_start) queryParams.period_start = params.period_start;
  if (params?.period_end) queryParams.period_end = params.period_end;
  if (params?.limit) queryParams.limit = params.limit.toString();
  if (params?.offset) queryParams.offset = params.offset.toString();

  return Make_Authorized_API_Call<SpecCost>(
    API_ENDPOINTS.SPECIFICATIONS.COSTS,
    'GET',
    undefined,
    { params: queryParams }
  );
}

/**
 * Get cost summary for a specification
 * GET /api/specifications/specificationCostSummaryGet
 */
export async function getSpecCostSummary(
  specificationId: string,
  params?: SpecCostQueryParams
): Promise<TimComboAPIResponse<SpecCostSummary>> {
  const queryParams: Record<string, string> = {
    Specification_ID: specificationId
  };

  if (params?.period_start) queryParams.period_start = params.period_start;
  if (params?.period_end) queryParams.period_end = params.period_end;
  if (params?.include_breakdown !== undefined) {
    queryParams.include_breakdown = params.include_breakdown.toString();
  }

  return Make_Authorized_API_Call<SpecCostSummary>(
    API_ENDPOINTS.SPECIFICATIONS.COST_SUMMARY,
    'GET',
    undefined,
    { params: queryParams }
  );
}

/**
 * Get recommendations for a specification
 * GET /api/specifications/specificationRecommendationsGet
 */
export async function getSpecRecommendations(
  specificationId: string,
  status?: 'pending' | 'acknowledged' | 'implemented' | 'dismissed'
): Promise<TimComboAPIResponse<SpecRecommendation>> {
  const queryParams: Record<string, string> = {
    Specification_ID: specificationId
  };

  if (status) queryParams.status = status;

  return Make_Authorized_API_Call<SpecRecommendation>(
    API_ENDPOINTS.SPECIFICATIONS.RECOMMENDATIONS,
    'GET',
    undefined,
    { params: queryParams }
  );
}

/**
 * Acknowledge/update a recommendation
 * PUT /api/specifications/specificationRecommendationAcknowledge
 */
export async function acknowledgeRecommendation(
  recommendationId: string,
  status: 'acknowledged' | 'implemented' | 'dismissed'
): Promise<TimComboAPIResponse<SpecRecommendation>> {
  return Make_Authorized_API_Call<SpecRecommendation>(
    API_ENDPOINTS.SPECIFICATIONS.RECOMMENDATION_ACKNOWLEDGE,
    'PUT',
    { Recommendation_ID: recommendationId, Status: status }
  );
}

/**
 * Get specification metrics for dashboard
 * GET /api/specifications/specificationMetricsGet
 */
export async function getSpecMetrics(
  clientId: string,
  projectId?: string,
  params?: SpecCostQueryParams
): Promise<TimComboAPIResponse<SpecMetrics>> {
  const queryParams: Record<string, string> = {
    Client_ID: clientId
  };

  if (projectId) queryParams.Project_ID = projectId;
  if (params?.period_start) queryParams.period_start = params.period_start;
  if (params?.period_end) queryParams.period_end = params.period_end;

  return Make_Authorized_API_Call<SpecMetrics>(
    API_ENDPOINTS.SPECIFICATIONS.METRICS,
    'GET',
    undefined,
    { params: queryParams }
  );
}

// =============================================================================
// CPO Helper Functions
// =============================================================================

/**
 * Helper to format currency for cost display
 */
export function formatCost(amount: number | undefined | null): string {
  if (amount === undefined || amount === null || isNaN(amount)) {
    return '$0.00';
  }
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(amount);
}

/**
 * Helper to get priority badge color for Flowbite Badge
 */
export function getPriorityColor(priority: 'high' | 'medium' | 'low'): 'failure' | 'warning' | 'info' | 'gray' {
  switch (priority) {
    case 'high':
      return 'failure';
    case 'medium':
      return 'warning';
    case 'low':
      return 'info';
    default:
      return 'gray';
  }
}

/**
 * Helper to get recommendation status badge color
 */
export function getRecommendationStatusColor(status: string): 'gray' | 'info' | 'success' | 'dark' {
  switch (status) {
    case 'pending':
      return 'gray';
    case 'acknowledged':
      return 'info';
    case 'implemented':
      return 'success';
    case 'dismissed':
      return 'dark';
    default:
      return 'gray';
  }
}

export default {
  importSpecifications,
  getSpecifications,
  getSpecification,
  createSpecification,
  updateSpecification,
  deleteSpecification,
  getSpecificationTemplates,
  createSpecificationTemplate,
  updateSpecificationTemplate,
  requestSpecificationReview,
  submitSpecificationApproval,
  getSpecificationApprovalStatus,
  embellishSpecification,
  linkSpecificationRepository,
  getSpecificationRepository,
  triggerCodebaseScan,
  generateSpecificationsFromCodebase,
  // Version History functions
  createSpecificationVersion,
  // Notification functions
  getSpecNotifications,
  // CPO functions
  getSpecCosts,
  getSpecCostSummary,
  getSpecRecommendations,
  acknowledgeRecommendation,
  getSpecMetrics,
  // Dependency Matrix functions
  getCodeDependencies,
  createCodeDependency,
  // CPO helpers
  formatCost,
  getPriorityColor,
  getRecommendationStatusColor,
};
